import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { Pagination } from 'toolkit';
import { HeroImage } from '../../components/DealerPage/components/HeroImage/HeroImage';
import { Breadcrumbs } from '../../components/ToolkitV2/Breadcrumbs/Breadcrumbs';
import type { Breadcrumb } from '../../components/ToolkitV2/Breadcrumbs/Breadcrumbs.typed';
import { StarRatingGroup } from '../../components/PageWrappers/AdDetails/components/GoogleReviews/StarRatingGroup/StarRatingGroup';
import { Dealer } from '../../domains/Dealer';

import {
  IDFP,
  IGetSection,
  ISearchPageAds,
  ISearchPageSeoData,
} from '../../api/types/searchPageApiTypes';
import { GTMEvent } from '../../services/gtmService';
import {
  useDealerPageDispatch,
  useDealerPageState,
} from '../../components/DealerPage/context/DealerPageContext';
import { useNextRouter } from '../../hooks/UseNextRouter';
import {
  useDealerFilterDispatch,
  useDealerFilterState,
} from '../../components/DealerPage/context/DealerFilterContext';
import ContactPage from '../../components/DealerPage/components/ContactPage/ContactPage';
import ReviewPage from '../../components/DealerPage/components/ReviewPage/ReviewPage';
import { useCookies } from '../../hooks/UseCookies';
import { useOnUpdateOnly } from '../../hooks/UseOnUpdateOnly';
import { cookies } from '../../constants/cookies';
import { useUserContext } from '../../contexts/UserContext';
import { useLocationContext } from '../../features/location/Location.context';
import { useDealerShowroomAds } from '../../components/DealerPage/hooks/useDealerShowroomAds';
import { useDealerPageGTM } from '../../components/DealerPage/hooks/useDealerPageGTM';
import { getDealerUrl } from '../../components/DealerPage/helpers/url';
import { useSectionDisplayInformation } from '../../components/DealerPage/hooks/useSectionDisplayInformation';
import { useDisplayFilters } from '../../components/DealerPage/hooks/useDisplayFilters';
import {
  mapFiltersRequest,
  mapGeoFilter,
  mapGeoFilterRequest,
  mapLegacyAreaFilterValues,
  mapRangesRequest,
} from '../../components/DealerPage/mappers/filters';
import { DetailsAndToggles } from '../../components/DealerPage/components/DetailsAndToggles/DetailsAndToggles';
import { dealerDirectoryQuickLinks } from '../DealerDirectory/queries/dealerDirectoryQuickLinks.query';

import { FiltersPanel } from '../../components/DealerPage/components/FiltersPanel/FiltersPanel';
import { SectionBrowser } from '../../components/DealerPage/components/SectionBrowser/SectionBrowser';
import { Filters } from '../../components/DealerPage/components/Filters/Filters';
import { Listings } from '../../components/DealerPage/components/Listings/Listings';
import * as SearchPageLayout from '../../components/DealerPage/styles';
import { NoResults } from '../../components/DealerPage/components/NoResults/NoResults';
import { FilterButton } from '../../components/DealerPage/components/FilterButton/FilterButton';
import { InternalLinks } from '../Toolkit/InternalLinks/InternalLinks';
import { Spacer } from '../../helpers/genericStyles';
import { Link } from '../../components/Toolkit/Button/Link';
import { StickyFooter } from '../../components/Toolkit/StickyFooter/StickyFooter';
import { Columns } from '../../components/Toolkit/Columns/Columns';
import { Button } from '../../components/Toolkit/Button';
import { formatNumber } from '../../helpers/formatting';
import * as Styled from '../../components/DealerPage/DealerPage.styled';
import { DealerContactDetails } from '../../components/DealerPage/components/DealerContactDetails/DealerContactDetails';
import {
  QUICK_LINKS_COUNTY_VALUE,
  QUICK_LINKS_FRANCHISE_VALUE,
  QUICK_LINKS_LIMIT,
} from './DealerPage.constants';

export enum DealerPages {
  our_stock = 'our_stock',
  contact = 'contact',
  reviews = 'reviews',
}

interface DealerPageProps {
  ads: ISearchPageAds[];
  sectionsData: IGetSection[];
  sectionName: string;
  parentName: string | null;
  parentDisplayName: string | null;
  searchMatches: number;
  dealer: Dealer;
  gtm: GTMEvent;
  dfp: IDFP;
  seoData: ISearchPageSeoData;
  cdnUrl: string;
  domain: string;
  page: DealerPages;
}

function DealerPage({
  cdnUrl,
  dealer,
  ads: adsData,
  gtm,
  dfp,
  seoData,
  sectionsData: initialSections,
  parentDisplayName: initialParentDisplayName,
  parentName: initialParentName,
  searchMatches: initialSearchMatches,
  sectionName: initialSectionName,
  domain,
  page: initialPage = DealerPages.our_stock,
}: DealerPageProps) {
  const [selectedTab, setTab] = useState(initialPage);
  const { updateUrlByHref, router } = useNextRouter();
  const {
    consentOptions: { functionalConsent },
  } = useUserContext();

  const { logo, address, googleReview, name } = dealer;

  const {
    setFilterAndRangeValues,
    setIsFilterModalOpen,
    populateMakeModelFilterWithOptions,
  } = useDealerFilterDispatch();

  const { keywordSearchTerm, section, viewType, paging, page, sort, userId } =
    useDealerPageState();
  const {
    setPageByDividing,
    setPageByTotal,
    setPaging,
    setSection,
    setPageInitial,
  } = useDealerPageDispatch();

  const { selectedCounty, coordinates, radius, countyTown } =
    useLocationContext();
  const { latitude, longitude } = coordinates;
  const county = selectedCounty?.value ?? null;

  const { filtersData } = useDisplayFilters({
    section,
    setFilterAndRangeValues,
    queryParams: router.query,
    userId,
    dealerSlug: dealer.slug,
    populateMakeModelFilterWithOptions,
  });

  const {
    filters,
    ranges,
    makeModel,
    isFilterModalOpen,
    appliedFiltersRangeCount,
  } = useDealerFilterState();

  const {
    ads = [],
    isLoading,
    gtm: gtmData,
    fetchAds,
  } = useDealerShowroomAds(dealer.slug, adsData, seoData, dfp, gtm, setPaging);

  const hasAds = ads && ads.length > 0;

  const gtmDealerPage = useDealerPageGTM(gtmData);

  function buildSeoParams() {
    const validRadius = countyTown ? radius : undefined;

    const { url, breadCrumbs } = getDealerUrl(
      section,
      {
        ...(userId && { userId }),
        ...(sort && { sort }),
        ...(keywordSearchTerm && { words: keywordSearchTerm }),
        ...(page && { start: (page * paging.pageSize).toString() }),
      },
      filters,
      makeModel,
      ranges,
      countyTown,
      validRadius,
      county,
    );

    const formattedUrl = `/dealer/${dealer.slug}${url}`;

    return {
      url: formattedUrl,
      seoBreadcrumbs: breadCrumbs,
    };
  }

  const {
    sections,
    sectionName,
    parentName,
    parentDisplayName,
    searchMatches,
    retrieveSections, // Todo set dealer here
  } = useSectionDisplayInformation({
    initialSections,
    initialSectionName,
    initialParentName,
    initialParentDisplayName,
    initialSearchMatches,
  });

  const previousSection =
    parentName && parentDisplayName
      ? {
          name: parentName,
          displayName: parentDisplayName,
        }
      : null;

  const viewTypeCookie = useCookies({ name: cookies.VIEW_TYPE });

  useOnUpdateOnly(() => {
    if (functionalConsent && viewType) {
      viewTypeCookie.set({ value: viewType });
    }
  }, [viewType]);

  useOnUpdateOnly(() => {
    setPageInitial();
  }, [filters]);

  useOnUpdateOnly(() => {
    window.scroll({ top: 0 });
  }, [page]);

  useOnUpdateOnly(() => {
    const locationData = mapGeoFilter({
      radius,
      latitude,
      longitude,
      county,
      countyTown,
    });
    fetchAds(
      [
        {
          name: 'dealerId',
          values: [dealer.id],
        },
        ...filters,
      ],
      ranges,
      makeModel,
      page,
      10,
      section,
      keywordSearchTerm,
      sort,
      userId,
      () => undefined,
      () => {
        const { url } = buildSeoParams();
        updateUrlByHref(url);
      },
      locationData?.geoFilter,
    );
    gtmDealerPage.searchPageEvent({ viewType: [viewType] });
  }, [
    filters,
    ranges,
    makeModel,
    sort,
    keywordSearchTerm,
    page,
    section,
    countyTown,
    selectedCounty,
    radius,
  ]);

  useOnUpdateOnly(() => {
    const geoFilterCounty = county ? [county] : null;
    const locationData = mapGeoFilter({
      radius,
      latitude,
      longitude,
      county: geoFilterCounty,
      countyTown,
    });
    if (!!sections?.length) {
      const mappedFilterValues = mapLegacyAreaFilterValues(
        filters,
        locationData?.geoFilter?.county,
        locationData?.geoFilter?.countyTown,
      );

      retrieveSections(section, {
        geoFilter: mapGeoFilterRequest(locationData?.geoFilter),
        filters: [
          {
            name: 'dealerId',
            values: [dealer.id],
          },
          ...mapFiltersRequest([...mappedFilterValues]),
        ],
        ...(ranges.length && { ranges: mapRangesRequest(ranges) }),
        paging: {
          pageSize: paging.pageSize,
          from: page * paging.pageSize,
        },
        sections: [section],
        terms: keywordSearchTerm ? keywordSearchTerm : null,
        sort,
      });
    }
  }, [
    filters,
    ranges,
    makeModel,
    keywordSearchTerm,
    section,
    selectedCounty,
    radius,
    countyTown,
  ]);

  const adCount = paging?.totalResults || 0;
  const resultOrResults = adCount === 1 ? 'result' : 'results';

  const isOurStockPage = selectedTab === DealerPages.our_stock;
  const isContactPage = selectedTab === DealerPages.contact;
  const isReviewsPage = selectedTab === DealerPages.reviews;

  const handleTabSelection = (tabValue: DealerPages) => {
    let pathname;
    if (tabValue === DealerPages.reviews)
      pathname = `/dealer/${dealer.slug}/${DealerPages.reviews}`;
    else if (tabValue === DealerPages.contact)
      pathname = `/dealer/${dealer.slug}/${DealerPages.contact}`;
    else pathname = buildSeoParams().url;
    router.push(
      {
        pathname,
      },
      undefined,
      { shallow: true },
    );
    setTab(tabValue);
  };

  const staticBreadcrumbs: Array<Breadcrumb> = [
    {
      displayName: 'Home',
      href: '/',
    },
    {
      displayName: 'All Dealers',
      href: '/find-a-dealer',
    },
  ];

  const fullBreadcrumbs: Array<Breadcrumb> = useMemo(
    () =>
      Boolean(sections?.length)
        ? [
            ...staticBreadcrumbs,
            {
              displayName: dealer.name,
            },
          ]
        : [
            ...staticBreadcrumbs,
            {
              displayName: dealer.name,
              href: `/dealer/${dealer.slug}`,
            },
            {
              displayName: sectionName as string,
            },
          ],
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [section, sectionName, selectedTab],
  );

  const { back } = useRouter();

  const { data: quickLinks } = useQuery(
    dealerDirectoryQuickLinks(
      QUICK_LINKS_COUNTY_VALUE,
      QUICK_LINKS_FRANCHISE_VALUE,
    ),
  );

  return (
    <>
      <HeroImage
        image={dealer.backgroundImage || ''}
        imageSmall={dealer.backgroundImageSmall || ''}
        cdnUrl={cdnUrl}
      >
        <div>
          <div className="row">
            <div className="columns small-12">
              <Breadcrumbs
                breadcrumbs={
                  isContactPage || isReviewsPage
                    ? [
                        ...staticBreadcrumbs,
                        {
                          displayName: dealer.name,
                          href: `/dealer/${dealer.slug}`,
                        },
                        {
                          displayName: isContactPage ? 'Contact' : 'Reviews',
                        },
                      ]
                    : [...fullBreadcrumbs]
                }
                goBack={back}
                inverted
              />
            </div>
          </div>
        </div>
        <Styled.DealerDetailsWrapper className="row set-width-100">
          <div className="columns small-12">
            <Styled.DealerDetails>
              {logo && <Styled.DealerLogo src={logo} />}
              <Styled.DealerInfo>
                <h1>{name}</h1>
                <Styled.DealerSubtext>
                  <span>{address}</span>
                  {googleReview?.rating && googleReview.displayReviews && (
                    <>
                      <Styled.DealerSubtextDivider />
                      <StarRatingGroup
                        rating={Number(googleReview.rating)}
                        numberOfStars={5}
                        multipleStars={false}
                        starWidth={'16'}
                        starHeight={'16'}
                      />
                    </>
                  )}
                </Styled.DealerSubtext>
              </Styled.DealerInfo>
            </Styled.DealerDetails>
          </div>
        </Styled.DealerDetailsWrapper>
      </HeroImage>
      <div className="row">
        <div className="columns small-12">
          <Styled.STabSelector
            selectedTab={selectedTab}
            tabs={[
              {
                label: <Styled.TabItem>Our Stock</Styled.TabItem>,
                value: DealerPages.our_stock,
                isDisabled: isOurStockPage,
              },
              {
                label: <Styled.TabItem>Contact Us</Styled.TabItem>,
                value: DealerPages.contact,
                isDisabled: isContactPage,
              },
              {
                label: <Styled.TabItem>Reviews</Styled.TabItem>,
                value: DealerPages.reviews,
                isDisabled: Boolean(dealer.googleReview) ? isReviewsPage : true,
              },
            ]}
            onSelectTab={handleTabSelection}
            variant={true}
          />
          {isOurStockPage && (
            <Styled.Main>
              <Styled.Sidebar>
                <FiltersPanel lastSearchData={undefined} showSearch={false}>
                  {!!sections?.length && (
                    <>
                      <SectionBrowser
                        currentSection={{
                          name: section,
                          displayName: sectionName,
                          searchMatches,
                        }}
                        sections={sections}
                        parentSection={previousSection}
                        baseUrl={`/dealer/${dealer.slug}`}
                        onSelectSection={(value) => setSection(value)}
                        onSelectBack={setSection}
                      />
                      <Spacer marginBottom={['M16']} />
                    </>
                  )}
                  <Filters
                    section={section}
                    filtersData={filtersData}
                    dealerId={dealer.id}
                  />
                  <DealerContactDetails
                    dealer={dealer}
                    cdnUrl={cdnUrl}
                    domain={''}
                  />
                </FiltersPanel>
              </Styled.Sidebar>
              <Styled.Body>
                <DetailsAndToggles
                  totalAds={paging.totalResults.toString()}
                  dealer={dealer}
                  cdnUrl={cdnUrl}
                  domain={''}
                />
                {hasAds ? (
                  <>
                    <Listings
                      listings={ads}
                      currentSection={section}
                      createSavedAdEvent={gtmDealerPage.createSavedAdEvent}
                      deleteSavedAdEvent={gtmDealerPage.deleteSavedAdEvent}
                      isLoading={isLoading}
                      viewType={viewType}
                    />
                    {paging?.totalPages > 1 && (
                      <SearchPageLayout.Pagination>
                        <Pagination
                          currentPage={paging.currentPage}
                          totalPages={paging.totalPages}
                          totalResults={paging.totalResults}
                          onUpdate={async (event) =>
                            setPageByDividing(event.from, paging.pageSize)
                          }
                          pageSize={paging.pageSize}
                          searchQueryGroup=""
                          goToEnd={() => setPageByTotal(paging.totalPages)}
                          goToStart={() => setPageByTotal(1)}
                        />
                      </SearchPageLayout.Pagination>
                    )}
                  </>
                ) : (
                  <NoResults
                    headerText="Sorry, there are no ads to show here"
                    subText={
                      <>
                        Check your filters or search{' '}
                        <Link href={`/find-a-dealer`} ofType="SECONDARY" inline>
                          All Dealers
                        </Link>
                      </>
                    }
                  />
                )}
                <SearchPageLayout.StickyFooter>
                  <StickyFooter>
                    <Columns>
                      <Button
                        ofType="SECONDARY"
                        onClick={() => handleTabSelection(DealerPages.contact)}
                      >
                        Contact Us
                      </Button>
                      <FilterButton
                        filterCount={appliedFiltersRangeCount}
                        onClick={() => setIsFilterModalOpen(true)}
                      />
                    </Columns>
                  </StickyFooter>
                </SearchPageLayout.StickyFooter>

                {isFilterModalOpen && (
                  <Styled.SBaseModal
                    askToClose={() => setIsFilterModalOpen(false)}
                    isOpen={isFilterModalOpen}
                    variant="WIDE"
                  >
                    <FiltersPanel showSearch={false}>
                      <Styled.SModalBody>
                        {!!sections?.length && (
                          <>
                            <SectionBrowser
                              currentSection={{
                                name: section,
                                displayName: sectionName,
                                searchMatches,
                              }}
                              sections={sections}
                              parentSection={previousSection}
                              onSelectSection={setSection}
                              onSelectBack={setSection}
                              baseUrl={`/dealer/${dealer.slug}`}
                              collapsible={true}
                            />
                            <Spacer marginBottom={['M16']} />
                          </>
                        )}
                        <Filters
                          filtersData={filtersData}
                          section={section}
                          dealerId={dealer.id}
                        />
                      </Styled.SModalBody>
                    </FiltersPanel>
                    <StickyFooter data-testid="sticky_footer_results">
                      <Button
                        onClick={() => {
                          window.scroll({ top: 0 });
                          setIsFilterModalOpen(false);
                        }}
                      >
                        Show {formatNumber(adCount)} {resultOrResults}
                      </Button>
                    </StickyFooter>
                  </Styled.SBaseModal>
                )}
              </Styled.Body>
            </Styled.Main>
          )}

          {isContactPage && (
            <ContactPage
              cdnUrl={cdnUrl}
              domain={domain}
              dealer={dealer}
              onReviewClick={() => handleTabSelection(DealerPages.reviews)}
            />
          )}

          {isReviewsPage && dealer.googleReview && (
            <ReviewPage
              cdnUrl={cdnUrl}
              domain={domain}
              dealer={dealer}
              ads={adsData}
            />
          )}

          {quickLinks && quickLinks.internalLinks.length > 0 ? (
            <>
              <InternalLinks
                title="Popular Searches"
                links={quickLinks.internalLinks}
                limit={QUICK_LINKS_LIMIT}
              />
              <Spacer marginBottom={['M24', 'L40']} />
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default DealerPage;
